import type { GameDefinition } from '../types/game';
import type { GameState } from './GameState';

/** Interfaz mínima de almacenamiento (permite inyectar uno en pruebas). */
export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Adaptador en memoria, útil cuando no hay localStorage (SSR, pruebas). */
export class MemoryStorage implements StorageAdapter {
  private map = new Map<string, string>();
  getItem(key: string): string | null {
    return this.map.has(key) ? this.map.get(key)! : null;
  }
  setItem(key: string, value: string): void {
    this.map.set(key, value);
  }
  removeItem(key: string): void {
    this.map.delete(key);
  }
}

function resolveDefaultStorage(): StorageAdapter {
  try {
    if (typeof localStorage !== 'undefined') return localStorage;
  } catch {
    /* entorno sin localStorage */
  }
  return new MemoryStorage();
}

/** Motivo por el que una partida guardada no puede reutilizarse. */
export type IncompatibleReason =
  | 'gameId'
  | 'gameVersion'
  | 'schemaVersion'
  | 'corrupt'
  | 'missingScene';

export interface LoadResult {
  state: GameState | null;
  reason?: IncompatibleReason;
}

/**
 * Persistencia de partidas en localStorage.
 *
 * Cada partida se identifica por gameId + gameVersion + instanceId, de modo
 * que pueden coexistir varias instancias del mismo juego en un futuro portal.
 */
export class SaveManager {
  private readonly storage: StorageAdapter;
  readonly key: string;

  constructor(
    private readonly game: GameDefinition,
    instanceId: string,
    storage?: StorageAdapter
  ) {
    this.storage = storage ?? resolveDefaultStorage();
    this.key = `adventurengine:${game.gameId}:${game.gameVersion}:${instanceId}`;
  }

  save(state: GameState): void {
    const stamped: GameState = { ...state, savedAt: Date.now() };
    try {
      this.storage.setItem(this.key, JSON.stringify(stamped));
    } catch (err) {
      console.error('[adventurengine] no se pudo guardar la partida', err);
    }
  }

  has(): boolean {
    return this.storage.getItem(this.key) !== null;
  }

  load(): LoadResult {
    const raw = this.storage.getItem(this.key);
    if (raw === null) return { state: null };
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { state: null, reason: 'corrupt' };
    }
    const state = parsed as GameState;
    if (!state || typeof state !== 'object' || !('gameId' in state)) {
      return { state: null, reason: 'corrupt' };
    }
    if (state.gameId !== this.game.gameId) return { state, reason: 'gameId' };
    if (state.gameVersion !== this.game.gameVersion) return { state, reason: 'gameVersion' };
    if (state.schemaVersion !== this.game.schemaVersion)
      return { state, reason: 'schemaVersion' };
    if (!(state.currentScene in this.game.scenes)) return { state, reason: 'missingScene' };
    // Partidas guardadas antes del marcador educativo: completar campos nuevos.
    if (!Array.isArray(state.presentedContents)) state.presentedContents = [];
    if (!state.exerciseResults || typeof state.exerciseResults !== 'object') {
      state.exerciseResults = {};
    }
    return { state };
  }

  clear(): void {
    this.storage.removeItem(this.key);
  }
}
